import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import NotificationFeedStream from "getstream";

import { GlobalState } from "@/Reducers/interface";
import { getUserGetStreamConfig } from "@/Contracts/Utils";
import {
	BannerFeedMessage,
	BannerFeedActivityResponse,
	BannerFeedActivitySubscribeResponse,
	BannerFeedActivity
} from "@/Contracts/Others/NotificationFeed";
import { dismissBannerNotification } from "@/Services/Others/BannerNotificationService";

export const GET_STREAM_BANNER_FEED_SLUG = "banner";

export enum BannerActionTypes {
	SHOW_BANNER_LOADER = "SHOW_BANNER_LOADER",
	HIDE_BANNER_LOADER = "HIDE_BANNER_LOADER",

	SHOW_BANNER = "SHOW_BANNER",
	HIDE_BANNER = "HIDE_BANNER",
	SET_BANNER_TEXT = "SET_BANNER_TEXT",

	DISMISS_BANNER_REQUESTED = "DISMISS_BANNER_REQUESTED",
	DISMISS_BANNER_SUCCESSFUL = "DISMISS_BANNER_SUCCESSFUL",
	DISMISS_BANNER_FAILED = "DISMISS_BANNER_FAILED",

	GET_BANNER_REQUESTED = "GET_BANNER_REQUESTED",
	GET_BANNER_SUCCESSFUL = "GET_BANNER_SUCCESSFUL",
	GET_BANNER_FAILED = "GET_BANNER_FAILED",

	SUBSCRIBE_BANNER_REQUESTED = "SUBSCRIBE_BANNER_REQUESTED",
	SUBSCRIBE_BANNER_SUCCESSFUL = "SUBSCRIBE_BANNER_SUCCESSFUL",
	SUBSCRIBE_BANNER_FAILED = "SUBSCRIBE_BANNER_FAILED",

	TOGGLE_PAYMENT_DUE_BANNER = "TOGGLE_PAYMENT_DUE_BANNER"
}

export type ShowBannerLoaderAction =
	Action<BannerActionTypes.SHOW_BANNER_LOADER>;
export type HideBannerLoaderAction =
	Action<BannerActionTypes.HIDE_BANNER_LOADER>;

export type ShowBannerAction = Action<BannerActionTypes.SHOW_BANNER>;
export type HideBannerAction = Action<BannerActionTypes.HIDE_BANNER>;
export type SetBannerTextAction = Action<BannerActionTypes.SET_BANNER_TEXT>;
export type DismissBannerRequestedAction =
	Action<BannerActionTypes.DISMISS_BANNER_REQUESTED>;
export type DismissBannerSuccessfulAction =
	Action<BannerActionTypes.DISMISS_BANNER_SUCCESSFUL>;
export type DismissBannerFailedAction =
	Action<BannerActionTypes.DISMISS_BANNER_FAILED>;

export type GetBannerRequestedAction =
	Action<BannerActionTypes.GET_BANNER_REQUESTED>;
export interface GetBannerSuccessfulAction
	extends Action<BannerActionTypes.GET_BANNER_SUCCESSFUL> {
	readonly bannerFeedMessage: BannerFeedMessage | null;
	readonly bannerFeedActivity: BannerFeedActivity | null;
}
export type GetBannerFailedAction = Action<BannerActionTypes.GET_BANNER_FAILED>;

export type SubscribeBannerRequestedAction =
	Action<BannerActionTypes.SUBSCRIBE_BANNER_REQUESTED>;
export type SubscribeBannerSuccessfulAction =
	Action<BannerActionTypes.SUBSCRIBE_BANNER_SUCCESSFUL>;
export type SubscribeBannerFailedAction =
	Action<BannerActionTypes.SUBSCRIBE_BANNER_FAILED>;

export interface TogglePaymentDueBannerAction
	extends Action<BannerActionTypes.TOGGLE_PAYMENT_DUE_BANNER> {
	readonly isPaymentDueBannerOpen: boolean;
}

export type BannerNotificationActions =
	| ShowBannerAction
	| HideBannerAction
	| SetBannerTextAction
	| DismissBannerRequestedAction
	| DismissBannerSuccessfulAction
	| DismissBannerFailedAction
	| ShowBannerLoaderAction
	| HideBannerLoaderAction
	| GetBannerRequestedAction
	| GetBannerSuccessfulAction
	| GetBannerFailedAction
	| SubscribeBannerRequestedAction
	| SubscribeBannerSuccessfulAction
	| SubscribeBannerFailedAction
	| TogglePaymentDueBannerAction;

export interface BannerNotificationDispatch {
	dismissBanner: ActionCreator<
		ThunkAction<
			Promise<DismissBannerSuccessfulAction>,
			GlobalState,
			null,
			DismissBannerSuccessfulAction
		>
	>;
	setBannerText: ActionCreator<
		ThunkAction<SetBannerTextAction, GlobalState, null, SetBannerTextAction>
	>;
	showBanner: ActionCreator<
		ThunkAction<ShowBannerAction, GlobalState, null, ShowBannerAction>
	>;
	hideBanner: ActionCreator<
		ThunkAction<HideBannerAction, GlobalState, null, HideBannerAction>
	>;
	showBannerLoader: ActionCreator<
		ThunkAction<
			ShowBannerLoaderAction,
			GlobalState,
			null,
			ShowBannerLoaderAction
		>
	>;
	hideBannerLoader: ActionCreator<
		ThunkAction<
			HideBannerLoaderAction,
			GlobalState,
			null,
			HideBannerLoaderAction
		>
	>;
	getBannerFeed: ActionCreator<
		ThunkAction<
			Promise<GetBannerSuccessfulAction>,
			GlobalState,
			null,
			GetBannerSuccessfulAction
		>
	>;
	subscribeBannerDetails: ActionCreator<
		ThunkAction<
			Promise<SubscribeBannerSuccessfulAction>,
			GlobalState,
			null,
			SubscribeBannerSuccessfulAction
		>
	>;
	togglePaymentDueBanner: ActionCreator<
		ThunkAction<
			TogglePaymentDueBannerAction,
			GlobalState,
			boolean,
			TogglePaymentDueBannerAction
		>
	>;
}

export const BannerNotificationActionCreators: BannerNotificationDispatch = {
	// this is called when a user has clicked on the dismiss button
	dismissBanner:
		() =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DismissBannerSuccessfulAction> => {
			try {
				// dispatching request action
				const dismissBannerRequestedAction: DismissBannerRequestedAction =
					{
						type: BannerActionTypes.DISMISS_BANNER_REQUESTED
					};
				dispatch(dismissBannerRequestedAction);
				const showBannerLoaderAction: ShowBannerLoaderAction = {
					type: BannerActionTypes.SHOW_BANNER_LOADER
				};
				dispatch(showBannerLoaderAction);

				const globalState = getState();
				const bannerMessage =
					globalState.bannerNotification.bannerFeedMessage;
				const bannerId = bannerMessage && bannerMessage.banner_id;

				if (!bannerMessage || !bannerId) {
					throw new Error("There are no active banner to dismiss");
				}

				const config = getUserGetStreamConfig(
					globalState.user.configurationSettings
				);

				if (!config || !config?.api_key || !config?.api_key.length) {
					throw new Error(
						"User does not have an api_key in his/her config"
					);
				}

				if (!globalState.user.userData.id?.length) {
					console.error(
						globalState.user.userData,
						"GetStream error when user id is not present in banner"
					);
				}

				const bannerFeedStreamConnection =
					NotificationFeedStream.connect(
						config?.api_key,
						config.token,
						config.app_id
					);

				const bannerNotificationFeed = bannerFeedStreamConnection.feed(
					GET_STREAM_BANNER_FEED_SLUG,
					globalState.user.userData.id,
					config.token
				);

				const activityId =
					globalState.bannerNotification.bannerFeedActivity?.id;
				if (!activityId) return Promise.reject();

				// Execute both the network calls in parallel.
				await Promise.all([
					bannerNotificationFeed.removeActivity(activityId),

					dismissBannerNotification({
						banner_id: bannerMessage.banner_id,
						banner_severity: bannerMessage.banner_severity,
						messages: bannerMessage.message,
						banner_type: bannerMessage.banner_type
					})
				]);

				// dispatching success action
				const dismissBannerSuccessfulAction: DismissBannerSuccessfulAction =
					{
						type: BannerActionTypes.DISMISS_BANNER_SUCCESSFUL
					};
				return dispatch(dismissBannerSuccessfulAction);
			} catch (error) {
				console.error(error);

				// dispatching error action
				const dismissBannerFailedAction: DismissBannerFailedAction = {
					type: BannerActionTypes.DISMISS_BANNER_FAILED
				};
				dispatch(dismissBannerFailedAction);

				return error;
			} finally {
				const hideBannerLoaderAction: HideBannerLoaderAction = {
					type: BannerActionTypes.HIDE_BANNER_LOADER
				};
				dispatch(hideBannerLoaderAction);
			}
		},

	setBannerText:
		() =>
		(dispatch: Dispatch): SetBannerTextAction => {
			const showBannerAction: SetBannerTextAction = {
				type: BannerActionTypes.SET_BANNER_TEXT
			};
			return dispatch(showBannerAction);
		},

	showBanner:
		() =>
		(dispatch: Dispatch): ShowBannerAction => {
			const showBannerAction: ShowBannerAction = {
				type: BannerActionTypes.SHOW_BANNER
			};
			return dispatch(showBannerAction);
		},

	// this is called when there is a callback form server to close the banner
	hideBanner:
		() =>
		(dispatch: Dispatch): HideBannerAction => {
			const hideBannerAction: HideBannerAction = {
				type: BannerActionTypes.HIDE_BANNER
			};
			return dispatch(hideBannerAction);
		},

	showBannerLoader:
		() =>
		(dispatch: Dispatch): ShowBannerLoaderAction => {
			const showBannerLoaderAction: ShowBannerLoaderAction = {
				type: BannerActionTypes.SHOW_BANNER_LOADER
			};
			return dispatch(showBannerLoaderAction);
		},

	hideBannerLoader:
		() =>
		(dispatch: Dispatch): HideBannerLoaderAction => {
			const hideBannerLoaderAction: HideBannerLoaderAction = {
				type: BannerActionTypes.HIDE_BANNER_LOADER
			};
			return dispatch(hideBannerLoaderAction);
		},

	getBannerFeed:
		() =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<GetBannerSuccessfulAction> => {
			try {
				const getBannerRequestedAction: GetBannerRequestedAction = {
					type: BannerActionTypes.GET_BANNER_REQUESTED
				};
				dispatch(getBannerRequestedAction);

				const globalState = getState();
				const config = getUserGetStreamConfig(
					globalState.user.configurationSettings
				);

				if (!config || !config?.api_key || !config?.api_key.length) {
					throw new Error(
						"User does not have an api_key in his/her config"
					);
				}

				if (!globalState.user.userData.id?.length) {
					console.error(
						globalState.user.userData,
						"GetStream error when user id is not present in banner"
					);
				}

				const bannerFeedStreamConnection =
					NotificationFeedStream.connect(
						config?.api_key,
						config.token,
						config.app_id
					);

				const bannerNotificationFeed = bannerFeedStreamConnection.feed(
					GET_STREAM_BANNER_FEED_SLUG,
					globalState.user.userData.id,
					config.token
				);

				const bannerFeedDataResponse =
					(await bannerNotificationFeed.get({
						limit: 1
					})) as BannerFeedActivityResponse;
				const activeBannerFeedData =
					bannerFeedDataResponse &&
					bannerFeedDataResponse.results &&
					bannerFeedDataResponse.results[0];

				const bannerFeedMessage: BannerFeedMessage | null =
					(!!activeBannerFeedData &&
						JSON.parse(activeBannerFeedData.details)) ||
					null;

				// show banner
				const getBannerSuccessfulAction: GetBannerSuccessfulAction = {
					type: BannerActionTypes.GET_BANNER_SUCCESSFUL,
					bannerFeedActivity: !!activeBannerFeedData
						? activeBannerFeedData
						: null,
					bannerFeedMessage
				};
				return dispatch(getBannerSuccessfulAction);
			} catch (error) {
				const getBannerFailedAction: GetBannerFailedAction = {
					type: BannerActionTypes.GET_BANNER_FAILED
				};
				dispatch(getBannerFailedAction);

				return Promise.reject(error);
			}
		},

	subscribeBannerDetails:
		() =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<SubscribeBannerSuccessfulAction> => {
			try {
				const subscribeBannerRequestedAction: SubscribeBannerRequestedAction =
					{
						type: BannerActionTypes.SUBSCRIBE_BANNER_REQUESTED
					};
				dispatch(subscribeBannerRequestedAction);

				const globalState = getState();
				const config = getUserGetStreamConfig(
					globalState.user.configurationSettings
				);

				if (!config || !config?.api_key || !config?.api_key.length) {
					throw new Error(
						"User does not have an api_key in his/her config"
					);
				}

				if (!globalState.user.userData.id?.length) {
					console.error(
						globalState.user.userData,
						"GetStream error when user id is not present in banner"
					);
				}

				const bannerFeedStreamConnection =
					NotificationFeedStream.connect(
						config.api_key,
						config.token,
						config.app_id
					);

				const bannerNotificationFeed = bannerFeedStreamConnection.feed(
					GET_STREAM_BANNER_FEED_SLUG,
					globalState.user.userData.id,
					config.token
				);

				await bannerNotificationFeed.subscribe(
					async (
						notificationLiveFeedResponse: BannerFeedActivitySubscribeResponse
					) => {
						const hasNewNotification =
							!!notificationLiveFeedResponse.new.length;
						const hasDeletedNotification =
							!!notificationLiveFeedResponse.deleted.length;

						if (hasNewNotification) {
							const activeBanner =
								notificationLiveFeedResponse.new &&
								notificationLiveFeedResponse.new[0];

							const bannerFeedMessage: BannerFeedMessage | null =
								(!!activeBanner &&
									JSON.parse(activeBanner.details)) ||
								null;

							const getBannerSuccessfulAction: GetBannerSuccessfulAction =
								{
									type: BannerActionTypes.GET_BANNER_SUCCESSFUL,
									bannerFeedMessage,
									bannerFeedActivity: activeBanner
								};
							dispatch(getBannerSuccessfulAction);
						}

						if (hasDeletedNotification) {
							const getBannerSuccessfulAction: GetBannerSuccessfulAction =
								{
									type: BannerActionTypes.GET_BANNER_SUCCESSFUL,
									bannerFeedMessage: null,
									bannerFeedActivity: null
								};
							dispatch(getBannerSuccessfulAction);
						}
					}
				);

				const subscribeBannerSuccessfulAction: SubscribeBannerSuccessfulAction =
					{
						type: BannerActionTypes.SUBSCRIBE_BANNER_SUCCESSFUL
					};
				return dispatch(subscribeBannerSuccessfulAction);
			} catch (error) {
				console.error(error);
				const subscribeBannerFailedAction: SubscribeBannerFailedAction =
					{
						type: BannerActionTypes.SUBSCRIBE_BANNER_FAILED
					};
				dispatch(subscribeBannerFailedAction);

				return Promise.reject(error);
			}
		},
	togglePaymentDueBanner:
		isPaymentDueBannerOpen =>
		(dispatch: Dispatch): TogglePaymentDueBannerAction => {
			const showBannerAction: TogglePaymentDueBannerAction = {
				type: BannerActionTypes.TOGGLE_PAYMENT_DUE_BANNER,
				isPaymentDueBannerOpen
			};
			return dispatch(showBannerAction);
		}
};

export default BannerNotificationActionCreators;
